import { Currencies } from "@/constants/currency";
import POSConfigModule from "@/store/modules/pos-config";
import { IReceiptPayment } from "@/interfaces/receipts";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { roundToNearestMultipleOf5 } from "../helpers/common";
import {
  OrderSession,
  Payment,
  PaymentMethod,
  PaymentState,
  TransactionType,
  VoucherType,
} from "@smeetz/orders";

/**
 * Resolve the printed name for a payment method. Swiss QR and Incert voucher use
 * dedicated i18n keys; every other method maps to `payment-method.<METHOD>`.
 */
function paymentMethodName(paymentMethod: PaymentMethod | string): string {
  if (paymentMethod === PaymentMethod.Swiss_QR_Code_Bank_Transfer) {
    return t("payment-method.swiss-qr-code-bank-transfer");
  }
  if (paymentMethod === PaymentMethod.EXTERNAL_INCERT_VOUCHER) {
    return t("payment-method.Incert_Voucher");
  }
  return t(`payment-method.${paymentMethod}`);
}

/** The payment method a redeemed voucher prints as, by its ledger type. */
function voucherPaymentMethod(voucherType?: VoucherType): PaymentMethod {
  switch (voucherType) {
    case VoucherType.GIFT_CARD:
      return PaymentMethod.POS_GIFT_CARD;
    case VoucherType.INCERT:
      return PaymentMethod.EXTERNAL_INCERT_VOUCHER;
    default:
      return PaymentMethod.VOUCHER;
  }
}

/**
 * @param orderSession the order session whose payments/refunds are printed
 * @returns payments done in the order session
 */
export function getReceiptPayments(
  orderSession: OrderSession
): IReceiptPayment[] {
  const receiptPayments: IReceiptPayment[] = [];
  const { payments } = orderSession;

  // A refund is recorded twice on the order stack: a negative PAYMENT row
  // and a REFUND row with the same method and value. The refund section
  // prints the REFUND row, so its negative PAYMENT twin must not print too.
  const refundKeys = new Set(
    payments
      .filter((payment) => payment.type === TransactionType.REFUND)
      .map((payment) => `${payment.paymentMethod}|${payment.value}`)
  );

  // 1- Handle payments accountings
  for (const payment of orderSession.payments) {
    if (
      payment.paymentMethod === PaymentMethod.Swiss_QR_Code_Bank_Transfer &&
      payment.state !== PaymentState.COMPLETED
    ) {
      continue;
    }
    if (
      payment.type === TransactionType.TAX ||
      payment.type === TransactionType.DISCOUNT ||
      payment.type === TransactionType.TICKET ||
      payment.type === TransactionType.REFUND ||
      payment.paymentMethod === PaymentMethod.FREE ||
      payment.state === PaymentState.FAILED
    ) {
      continue;
    }
    if (
      payment.value < 0 &&
      refundKeys.has(`${payment.paymentMethod}|${payment.value}`)
    ) {
      continue;
    }
    if (
      payment.paymentMethod === PaymentMethod.CASH &&
      payment.multiCurrencyInfo?.paymentCurrency &&
      payment.currency !== payment.multiCurrencyInfo?.paymentCurrency &&
      payment.multiCurrencyInfo?.providedCashBill
    ) {
      /*
       if payment was in secondary currency we print the provided
       cash bill in that currency and change given back in main currency
      */
      const { multiCurrencyInfo } = payment;
      let value =
        multiCurrencyInfo.providedCashBill *
        (1 / (multiCurrencyInfo.exchangeRate || 1));
      if (payment.currency === Currencies.CHF) {
        value = roundToNearestMultipleOf5(Math.round(value));
      }
      receiptPayments.push({
        name: paymentMethodName(payment.paymentMethod),
        providedCashBill: (multiCurrencyInfo.providedCashBill / 100).toFixed(2),
        exchangeRate: (multiCurrencyInfo.exchangeRate || 1).toFixed(2),
        value: (value / 100).toFixed(2),
        currency:
          multiCurrencyInfo.paymentCurrency || payment.currency || "CHF",
      });
    } else if (
      payment.paymentMethod === PaymentMethod.CASH &&
      payment.multiCurrencyInfo?.paymentCurrency &&
      payment.currency === payment.multiCurrencyInfo.paymentCurrency &&
      payment.multiCurrencyInfo.paymentCurrency
    ) {
      const { multiCurrencyInfo } = payment;
      // if paid in cash with main currency then
      // print the provided cash bill of the main currency
      // and the change given back (if any) by substracting the accounting entry values
      const { value } = payment;
      receiptPayments.push({
        name: paymentMethodName(payment.paymentMethod),
        value: (multiCurrencyInfo.providedCashBill / 100).toFixed(2),
        currency:
          multiCurrencyInfo.paymentCurrency || payment.currency || "CHF",
      });
      const changeValue = multiCurrencyInfo.providedCashBill - value;
      if (changeValue > 0) {
        // only print if there was a change
        receiptPayments.push({
          name: t(`multicurrencies.change`),
          value: ((changeValue * -1) / 100).toFixed(2),
          currency: payment.currency || "CHF",
        });
      }
    } else {
      const { multiCurrencyInfo } = payment;
      // otherwise print the payment method with value of accounting entries
      const { value } = payment;
      receiptPayments.push({
        name: paymentMethodName(payment.paymentMethod),
        value: (value / 100).toFixed(2),
        currency:
          multiCurrencyInfo?.paymentCurrency || payment.currency || "CHF",
      });
    }
  }
  // 2- Vouchers redeemed as payment (gift cards, Smeetz and Incert vouchers)
  // live in `orderSession.vouchers`; value is in major units and signed.
  for (const voucher of orderSession.vouchers ?? []) {
    const value = Number(voucher.value || 0);
    if (value === 0) {
      continue;
    }
    receiptPayments.push({
      name: paymentMethodName(voucherPaymentMethod(voucher.voucherType)),
      value: value.toFixed(2),
      currency: orderSession.currency || POSConfigModule.getGroupCurrency,
    });
  }

  // 3- Now handle refund acountings
  const refunds = payments.filter(
    (payment) => payment.type === TransactionType.REFUND
  );
  const groupedRefunds: {
    [method: string]: Payment[];
  } = {};
  // Lets group refund accountings based on their paymentMethods
  for (const refund of refunds) {
    if (groupedRefunds[refund.paymentMethod]) {
      // This payment method is already grouped lets push this ac
      groupedRefunds[refund.paymentMethod].push(refund);
    } else {
      groupedRefunds[refund.paymentMethod] = [];
      groupedRefunds[refund.paymentMethod].push(refund);
    }
  }
  // Now foreach grouped accountings we will calculate the sum of their accounting entries
  for (const paymentMethod in groupedRefunds) {
    let value = 0;
    groupedRefunds[paymentMethod]
      .map((refund) => refund.value)
      .forEach((refundValue) => {
        value += refundValue;
      });
    receiptPayments.push({
      name: paymentMethodName(paymentMethod),
      value: (value / 100).toFixed(2),
      currency: groupedRefunds[paymentMethod][0].currency || "CHF",
    });
  }
  return receiptPayments;
}

export function getCurrencySymbol(currency: Currencies) {
  switch (currency) {
    case Currencies.CHF:
      return "₣";
    case Currencies.EUR:
      return "€";
    case Currencies.GBP:
      return "£";
    case Currencies.USD:
      return "$";
    default:
      return currency;
  }
}
